import { Button, TextInput, useMutationWithToast } from "@repo/ui";
import { useForm } from "react-hook-form";
import {
  LockClosedIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthenticationService } from "../../services/index";
import { useState, useEffect } from "react";

type ResetPasswordFormType = {
  newPassword: string;
  confirmPassword: string;
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export function ResetPassword() {
  const resetPasswordForm = useForm<ResetPasswordFormType>({ mode: "onBlur" });
  const resetPasswordMutation = useMutationWithToast(
    AuthenticationService.resetPasswordWithToken,
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      navigate("/forgot-password");
    }
  }, [searchParams, navigate]);

  const newPassword = resetPasswordForm.watch("newPassword");

  const onSubmit = resetPasswordForm.handleSubmit((formData) => {
    if (!token) return;
    if (formData.newPassword !== formData.confirmPassword) {
      resetPasswordForm.setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }
    resetPasswordMutation.mutate(
      { token, new_password: formData.newPassword },
      {
        onSuccess() {
          setIsPasswordReset(true);
        },
      },
    );
  });

  const rules = [
    { label: "At least 8 characters", ok: (newPassword?.length ?? 0) >= 8 },
    { label: "One uppercase letter", ok: /[A-Z]/.test(newPassword || "") },
    { label: "One lowercase letter", ok: /[a-z]/.test(newPassword || "") },
    { label: "One number", ok: /[0-9]/.test(newPassword || "") },
  ];

  if (isPasswordReset) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <motion.div {...fadeUp} className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10 text-center">
            <div className="w-16 h-16 bg-success-light rounded-full flex items-center justify-center mb-6 mx-auto">
              <CheckCircleIcon className="h-8 w-8 text-success-main" />
            </div>
            <h2
              className="font-bold text-gray-900 mb-2"
              style={{ fontSize: "1.6rem" }}
            >
              Password reset!
            </h2>
            <p className="text-gray-500 text-md mb-8">
              Your password has been successfully reset. You can now sign in
              with your new password.
            </p>
            <Link to="/login" className="w-full">
              <Button className="w-full">Continue to Login</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <motion.div {...fadeUp} className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10 text-center">
            <div className="w-16 h-16 bg-error-light rounded-full flex items-center justify-center mb-6 mx-auto">
              <ExclamationTriangleIcon className="h-8 w-8 text-error-main" />
            </div>
            <h2
              className="font-bold text-gray-900 mb-2"
              style={{ fontSize: "1.6rem" }}
            >
              Invalid link
            </h2>
            <p className="text-gray-500 text-md mb-8">
              This password reset link is invalid or has expired. Please request
              a new one.
            </p>
            <Link to="/forgot-password" className="w-full">
              <Button className="w-full">Request New Link</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <motion.div {...fadeUp} className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10">
          <div className="mb-8">
            <h2
              className="font-bold text-gray-900 mb-1"
              style={{ fontSize: "1.5rem" }}
            >
              Set new password
            </h2>
            <p className="text-gray-500 text-md">
              Choose a strong password for your account.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <TextInput
              label="New password"
              placeholder="Enter your new password"
              fullWidth
              control={resetPasswordForm.control}
              name="newPassword"
              type="password"
              startAdornment={<LockClosedIcon className="h-5 w-5" />}
            />

            <TextInput
              label="Confirm password"
              placeholder="Confirm your new password"
              fullWidth
              control={resetPasswordForm.control}
              name="confirmPassword"
              type="password"
              startAdornment={<LockClosedIcon className="h-5 w-5" />}
            />

            {/* Password strength indicators */}
            <div className="flex flex-col gap-1.5">
              {rules.map((r) => (
                <div
                  key={r.label}
                  className={`flex items-center gap-2 text-sm transition-colors ${r.ok ? "text-success-main" : "text-gray-400"}`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.ok ? "bg-success-main" : "bg-gray-300"}`}
                  />
                  {r.label}
                </div>
              ))}
            </div>

            <Button
              loading={resetPasswordMutation.isPending}
              type="submit"
              className="w-full"
            >
              Reset Password
            </Button>
          </form>

          <div className="mt-4">
            <Link to="/login">
              <Button variant="text" className="w-full">
                <span className="flex items-center justify-center gap-2">
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to Login
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
